import React, { useState } from "react";
import { AppConfig, PanelConfig } from "../../types/config";
import config from "../../config/config.json";
import { ToggleSwitch } from "./ToggleSwitch";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import SettingsIcon from "@mui/icons-material/Settings";
import { PanelConfigUIProps } from "../../types/config";
import type { SplitPanelTheme } from "./SplitPanel";

const DEFAULT_PANEL_CONFIGS: PanelConfig[] = [
  { id: "topPanel", label: "Top", defaultSplit: "h", defaultCollapseDirection: "up", initialSize: 56, maxResize: 56, snapButtonTimeout: 300, style: {} },
  { id: "bottomPanel", label: "Bottom", defaultSplit: "h", defaultCollapseDirection: "down", initialSize: 300, maxResize: 600, snapButtonTimeout: 300, style: {} },
  { id: "leftPanel", label: "Left", defaultSplit: "v", defaultCollapseDirection: "left", initialSize: 380, maxResize: 600, snapButtonTimeout: 300, style: {} },
  { id: "rightPanel", label: "Right", defaultSplit: "v", defaultCollapseDirection: "right", initialSize: 320, maxResize: 600, snapButtonTimeout: 300, style: {} },
];

const DEFAULT_APP_CONFIG: AppConfig = {
  panelConfigs: DEFAULT_PANEL_CONFIGS,
  splitPanelTheme: {} as SplitPanelTheme,
  collapseOptions: { h: [], v: [] },
  settingsSummary: "Panel settings",
};

const rawConfig = config as unknown as Partial<AppConfig>;
// Merge per-panel-id rather than replacing the whole list: a county's config might only
// override some panels (or predate a panel that got added later, e.g. rightPanel) - falling
// back wholesale to DEFAULT_PANEL_CONFIGS when *any* panelConfigs is present would silently
// drop the ones it doesn't mention, crashing every .find(...)! call site downstream.
const configuredPanels = rawConfig.panelConfigs ?? [];
const mergedPanelConfigs: PanelConfig[] = DEFAULT_PANEL_CONFIGS.map(
  (def) => configuredPanels.find((p) => p.id === def.id) ?? def
);
const appConfig: AppConfig = {
  ...DEFAULT_APP_CONFIG,
  panelConfigs: mergedPanelConfigs,
  splitPanelTheme: (rawConfig.splitPanelTheme ?? DEFAULT_APP_CONFIG.splitPanelTheme) as SplitPanelTheme,
  collapseOptions: rawConfig.collapseOptions ?? DEFAULT_APP_CONFIG.collapseOptions,
  settingsSummary: rawConfig.settingsSummary ?? DEFAULT_APP_CONFIG.settingsSummary,
};
export const panelConfigs: PanelConfig[] = appConfig.panelConfigs;
export const PanelConfigUI: React.FC<PanelConfigUIProps> = ({
  panelCollapseDirections,
  onCollapseDirectionChange,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <div>
      <IconButton onClick={handleOpen} aria-label="settings">
        <SettingsIcon />
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <div style={{ padding: "10px", minWidth: "150px" }}>
          <div style={{ marginBottom: "10px", fontWeight: "bold" }}>
            {appConfig.settingsSummary}
          </div>
          {panelConfigs.map((cfg) => {
            const isHorizontal = cfg.defaultSplit === "h";
            const current = panelCollapseDirections[cfg.id];
            const isOn = isHorizontal ? current === "down" : current === "right";
            const onLabel = isHorizontal ? "D" : "R";
            const offLabel = isHorizontal ? "U" : "L";
            const toggle = () => {
              const newDirection = isOn
                ? isHorizontal
                  ? "up"
                  : "left"
                : isHorizontal
                  ? "down"
                  : "right";
              onCollapseDirectionChange(cfg.id, newDirection);
            };
            return (
              <div
                key={cfg.id}
                style={{
                  margin: "5px 0",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span style={{ marginRight: "10px", width: "100px" }}>
                  {cfg.label}:
                </span>
                <ToggleSwitch
                  isOn={isOn}
                  onToggle={toggle}
                  onLabel={onLabel}
                  offLabel={offLabel}
                />
              </div>
            );
          })}
        </div>
      </Menu>
    </div>
  );
};

export default appConfig;
